if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput;
      quantityForm;
      onVariantChangeUnsubscriber;
      cartUpdateUnsubscriber;
      abortController;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.bindAIGalleryThumbToVariant();

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      /* =========================================================
         VARIANT CHANGE → PRODUCT INFO UPDATE
         ========================================================= */
      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl),
        });
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((r) => r.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            callback(html);
          })
          .then(() => document.querySelector(`#${targetId}`)?.focus())
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          if (!variant) return;

          this.updateVariantInputs(variant.id);
          this.updateURL(productUrl, variant.id);
          this.updateAddToCartButton(html);

          // 🔥 THIS IS THE IMPORTANT PART
          this.updateMedia(html, variant.featured_media?.id);
        };
      }

      getSelectedVariant(html) {
        const el = html.querySelector('variant-selects [data-selected-variant]');
        return el ? JSON.parse(el.innerHTML) : null;
      }

      buildRequestUrlWithParams(url, optionValues) {
        const params = [`section_id=${this.sectionId}`];
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll('form[action*="/cart"] input[name="id"]').forEach((input) => {
          input.value = variantId;
          const ev = new Event('change', { bubbles: true });
ev.__fromGalleryThumb = true;
input.dispatchEvent(ev);

        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}?variant=${variantId}`);
      }

      /* =========================================================
         ADD TO CART BUTTON SYNC
         Mirrors the submit button's disabled state and label, and
         the hidden variant id input's disabled state, straight from
         the section HTML buy-buttons.liquid just rendered for the
         newly selected variant. Deliberately does not recompute
         availability or quantity_rule_soldout here: that logic
         lives in buy-buttons.liquid and reading its output keeps
         this in sync automatically if that logic ever changes.
         Mutates the existing button/input nodes in place rather
         than replacing them, so any listeners already bound to
         them (including the MS-230 bundle add-to-cart logic in
         product-form.js) are left intact.
         ========================================================= */
      updateAddToCartButton(html) {
        const buttonId = `ProductSubmitButton-${this.sectionId}`;
        const liveButton = this.querySelector(`#${buttonId}`);
        const fetchedButton = html.querySelector(`#${buttonId}`);
        if (liveButton && fetchedButton) {
          liveButton.disabled = fetchedButton.hasAttribute('disabled');

          const liveLabel = liveButton.querySelector('span');
          const fetchedLabel = fetchedButton.querySelector('span');
          if (liveLabel && fetchedLabel) {
            liveLabel.textContent = fetchedLabel.textContent;
          }
        }

        const liveIdInput = this.querySelector('form[action*="/cart"] input.product-variant-id');
        const fetchedIdInput = html.querySelector('form[action*="/cart"] input.product-variant-id');
        if (liveIdInput && fetchedIdInput) {
          liveIdInput.disabled = fetchedIdInput.hasAttribute('disabled');
        }
      }

      /* =========================================================
         MEDIA SYNC
         ========================================================= */
      updateMedia(html, mediaId) {
        if (!mediaId) return;

        // 1️⃣ Dawn desktop gallery
        this.querySelector('media-gallery')?.setActiveMedia?.(
          `${this.dataset.section}-${mediaId}`,
          true
        );

        // 2️⃣ AI mobile gallery
        this.syncAIGalleriesToMediaId(mediaId);
      }

      syncAIGalleriesToMediaId(mediaId) {
        const galleries = Array.from(document.querySelectorAll('*')).filter((el) =>
          el.tagName.toLowerCase().startsWith('product-image-gallery-')
        );

        galleries.forEach((gallery) => {
          const thumb = gallery.querySelector(
            `[data-media-id="${CSS.escape(String(mediaId))}"][data-index]`
          );
          if (thumb) {
            thumb.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
          }
        });
      }

      /* =========================================================
         AI THUMB → VARIANT SELECTION
         ========================================================= */
      bindAIGalleryThumbToVariant() {
        if (this.__aiThumbBound) return;
        this.__aiThumbBound = true;

        document.addEventListener(
          'click',
          (e) => {
            const thumb = e.target.closest('[data-media-id][class*="ai-product-gallery-thumbnail-"]');
            if (!thumb) return;

            const mediaId = thumb.getAttribute('data-media-id');
            const variant = this.getVariantForMediaId(mediaId);
            if (!variant) return;

            this.applyVariant(variant);
          },
          true
        );
      }

      getVariantCaches() {
        if (this.__variantCaches) return this.__variantCaches;

        const productJson = JSON.parse(
          document.getElementById(`product-data-${this.sectionId}`)?.textContent || '{}'
        );

        const mediaMap = JSON.parse(
          document.getElementById(`MediaVariantMap-${this.sectionId}`)?.textContent || '[]'
        );

        const variantsById = new Map(productJson.variants.map((v) => [String(v.id), v]));
        const mediaToVariants = new Map(
          mediaMap.map((m) => [String(m.media_id), m.variant_ids.map(String)])
        );

        this.__variantCaches = { productJson, variantsById, mediaToVariants };
        return this.__variantCaches;
      }

      getVariantForMediaId(mediaId) {
        const { variantsById, mediaToVariants } = this.getVariantCaches();
        const ids = mediaToVariants.get(String(mediaId));
        if (!ids?.length) return null;

        const variants = ids.map((id) => variantsById.get(id)).filter(Boolean);
        return variants.find((v) => v.available) || variants[0] || null;
      }

      applyVariant(variant) {
        const { productJson } = this.getVariantCaches();

        const radios = this.querySelector('variant-radios');
        const selects = this.querySelector('variant-selects');

        if (radios) {
          variant.options.forEach((value, idx) => {
            const optionName = productJson.options[idx];
            const input = radios.querySelector(
              `[name="options[${CSS.escape(optionName)}]"][value="${CSS.escape(value)}"]`
            );
            if (input && !input.checked) {
              input.checked = true;
              const ev = new Event('change', { bubbles: true });
ev.__fromGalleryThumb = true;
input.dispatchEvent(ev);

            }
          });
          return;
        }

        if (selects) {
          selects.querySelectorAll('select').forEach((sel, idx) => {
            if (sel.value !== variant.options[idx]) {
              sel.value = variant.options[idx];
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      }

      /* ========================================================= */
      initQuantityHandlers() {}
      initializeProductSwapUtility() {}
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}